/**
 * react-query wiring for `/api/flashcards/*` (brief §flashcards). Query keys
 * are namespaced `["flashcards", …]` per `docs/CONVENTIONS.md`.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@client/api/client";
import { useTopicsAndChapters } from "@client/api/topics";
import type {
  DecksResponse,
  QueueResponse,
  ReviewBody,
  ReviewResponse,
  StatsResponse,
} from "./types";

export const flashcardsKeys = {
  all: ["flashcards"] as const,
  decks: () => ["flashcards", "decks"] as const,
  stats: () => ["flashcards", "stats"] as const,
};

export function useDecks() {
  return useQuery({
    queryKey: flashcardsKeys.decks(),
    queryFn: () => api.get<DecksResponse>("/api/flashcards/decks"),
  });
}

export function useFlashcardStats() {
  return useQuery({
    queryKey: flashcardsKeys.stats(),
    queryFn: () => api.get<StatsResponse>("/api/flashcards/stats"),
  });
}

/**
 * Chapters + topics for the deck picker's topic filter. Shared hook (see
 * `@client/api/topics`) so every feature hits one cache entry.
 */
export { useTopicsAndChapters as useTopics };

export interface StartQueueParams {
  chapter?: number;
  topic?: string;
  limit?: number;
  kind?: "all" | "due" | "new";
}

function queueQueryString(params: StartQueueParams): string {
  const search = new URLSearchParams();
  if (params.chapter !== undefined) search.set("chapter", String(params.chapter));
  if (params.topic) search.set("topic", params.topic);
  if (params.kind !== undefined) search.set("kind", params.kind);
  search.set("limit", String(params.limit ?? 30));
  return search.toString();
}

/** Fetches one review queue — invoked from the deck picker's action buttons. */
export function useStartQueue() {
  return useMutation({
    mutationFn: (params: StartQueueParams) =>
      api.get<QueueResponse>(`/api/flashcards/queue?${queueQueryString(params)}`),
  });
}

export function useReviewCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ReviewBody) => api.post<ReviewResponse>("/api/flashcards/review", body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: flashcardsKeys.all });
    },
  });
}
